import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import StyledInput from '../styled/StyledInput';
import { Control, Path, PathValue, useWatch } from 'react-hook-form';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  ExtendedTarificationFormData,
  TarificationFormData,
  TarificationSchema,
} from '@/types/benefits/tarification/TarificationFormData';
import { BenefitType } from '@/types/benefits/benefitTypes';
import NestedTarifPeriods from './NestedTarifPeriods';
import NestedTarifRepartition from './NestedTarifRepartition';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import { useTranslation } from '@/i18n';

interface TarifCardProps<FieldsType extends ExtendedTarificationFormData> {
  control: Control<FieldsType>;
  index: number;
  deleteTarif: () => void;
  duplicateTarif: () => void;
  tarifData?: TarificationFormData;
  benefitType?: BenefitType;
  tarifType?: 'par-prestation' | 'par-personne';
  customTarif?: boolean;
  isViewMode?: boolean;
}

const TarifCard = <FieldsType extends ExtendedTarificationFormData>({
  control,
  index,
  deleteTarif,
  duplicateTarif,
  tarifData,
  benefitType = BenefitType.ACTIVITY,
  tarifType = 'par-prestation',
  customTarif = false,
  isViewMode = false,
}: TarifCardProps<FieldsType>) => {
  const { t } = useTranslation();

  const [isFolded, setIsFolded] = useState(false);

  const currentTitle = useWatch({
    control,
    name: `tarifs.${index}.name` as Path<FieldsType>,
    defaultValue: tarifData?.name as PathValue<FieldsType, Path<FieldsType>>,
  });

  const { error } = TarificationSchema.safeParse(tarifData);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle
          className="text-base leading-6 hover:cursor-pointer"
          onClick={() => setIsFolded(!isFolded)}
        >
          {(currentTitle as string) ||
            t('tariffs.tarif_', { index: index + 1 })}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {!isViewMode && (
              <StyledTooltipContainer tooltipMessage={t('common.duplicate')}>
                <CopyIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => {
                    setIsFolded(true);
                    duplicateTarif();
                  }}
                />
              </StyledTooltipContainer>
            )}
            {!isViewMode && (
              <StyledTooltipContainer tooltipMessage={t('common.delete')}>
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => deleteTarif()}
                />
              </StyledTooltipContainer>
            )}
          </div>
          {isFolded ? (
            <ChevronDown
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(false)}
            />
          ) : (
            <ChevronUp
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(true)}
            />
          )}
        </div>
      </CardHeader>
      {!isFolded && (
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-col">
            {error && !isViewMode && (
              <>
                {error.issues
                  .filter(({ code }) => code === 'custom')
                  .map(({ message }, indexError) => (
                    <p
                      className="text-sm leading-6 font-medium text-error-500"
                      key={`error-${indexError}`}
                    >
                      {message}
                    </p>
                  ))}
              </>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <StyledInput
              control={control}
              name={`tarifs.${index}.name` as Path<FieldsType>}
              placeholder={t('tariffs.placeholder')}
              disabled={isViewMode}
            />
            <NestedTarifPeriods
              control={control}
              tarifIndex={index}
              isViewMode={isViewMode}
            />
            <NestedTarifRepartition
              control={control}
              tarifIndex={index}
              customTarif={customTarif}
              tarifType={tarifType}
              isViewMode={isViewMode}
              type={benefitType}
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default TarifCard;
